// src/components/HR/SalaryManagementSection.js (FINAL CORRECTED VERSION)

import React, { useState, useEffect } from 'react';
import { useFirebase } from '../../App';
import { collection, query, where, orderBy, onSnapshot, getDocs, doc, setDoc, updateDoc, Timestamp, addDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { LoadingState } from '../Common/PanelState';

// --- Internal Component: SalaryEditModal ---
function SalaryEditModal({ isOpen, onClose, onSave, salaryRecord, employee }) {
    const [deductions, setDeductions] = useState(salaryRecord?.deductions || 0);
    const [bonus, setBonus] = useState(salaryRecord?.bonus || 0);
    const [notes, setNotes] = useState(salaryRecord?.notes || '');
    const [netSalary, setNetSalary] = useState(salaryRecord?.netSalary || employee.salary || 0);

    useEffect(() => {
        const basic = employee.salary || 0;
        const newNetSalary = basic + parseFloat(bonus || 0) - parseFloat(deductions || 0);
        setNetSalary(newNetSalary);
    }, [deductions, bonus, employee.salary]);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave({
            ...salaryRecord,
            deductions: parseFloat(deductions || 0),
            bonus: parseFloat(bonus || 0),
            netSalary,
            notes,
            status: salaryRecord.status === 'Paid' ? 'Paid' : 'Processed'
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-lg">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Edit Salary for {employee.fullName}</h2>
                <p className="text-gray-500 mb-6">For {new Date(salaryRecord.monthId.split('-')[0], salaryRecord.monthId.split('-')[1]-1).toLocaleString('default', { month: 'long', year: 'numeric' })}</p>
                <div className="space-y-4">
                     <div><label>Basic Salary (PKR)</label><input type="text" value={(employee.salary || 0).toLocaleString()} className="mt-1 w-full p-3 bg-gray-200 rounded-lg" readOnly /></div>
                     <div><label>Bonus / Allowance</label><input type="number" id="bonus" value={bonus} onChange={e => setBonus(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" /></div>
                     <div><label>Deductions</label><input type="number" id="deductions" value={deductions} onChange={e => setDeductions(e.target.value)} className="mt-1 w-full p-3 bg-gray-50 rounded-lg border" /></div>
                     <div><label>Notes (Optional)</label><textarea id="notes" value={notes} onChange={e => setNotes(e.target.value)} rows="2" className="mt-1 w-full p-3 bg-gray-50 rounded-lg border"></textarea></div>
                    <div className="pt-4 border-t"><div className="flex justify-between items-center text-xl font-bold"><span>Net Salary:</span><span className="text-red-600" style={{color: '#c4151c'}}>PKR {netSalary.toLocaleString()}</span></div></div>
                </div>
                <div className="flex justify-end gap-4 mt-8">
                    <button onClick={onClose} className="px-5 py-2 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSave} className="px-5 py-2 bg-red-600 text-white rounded-lg font-semibold" style={{backgroundColor: '#c4151c'}}>Save Changes</button>
                </div>
            </div>
        </div>
    );
}

// --- Main Component: SalaryManagementSection ---
function SalaryManagementSection() {
    const { db } = useFirebase();
    const [employees, setEmployees] = useState([]);
    const [salaryData, setSalaryData] = useState({});
    const [attendanceSummary, setAttendanceSummary] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    
    const [filter, setFilter] = useState({ month: new Date().getMonth(), year: new Date().getFullYear() });
    const [editingSalaryRecord, setEditingSalaryRecord] = useState(null);
    const toYYYYMMDD = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

    useEffect(() => {
        if (!db) { setEmployees([]); setIsLoading(false); return; }
        setIsLoading(true);
        const q = query(collection(db, `workers`), orderBy("fullName"));
        const unsub = onSnapshot(q, (snapshot) => { setEmployees(snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }))); setIsLoading(false); }, () => setIsLoading(false));
        return () => unsub();
    }, [db]);
    
    useEffect(() => {
        if (employees.length === 0) { setSalaryData({}); setAttendanceSummary({}); return; }

        const salaryMonthId = `${filter.year}-${String(filter.month + 1).padStart(2, '0')}`;
        const salaryQuery = query(collection(db, `salaries`), where("monthId", "==", salaryMonthId));
        const unsubSalaries = onSnapshot(salaryQuery, (snapshot) => { const salaries = {}; snapshot.forEach(doc => { salaries[doc.data().employeeId] = doc.data(); }); setSalaryData(salaries); });

        const fetchAttendance = async () => {
            const startDateOfMonth = new Date(filter.year, filter.month, 1);
            const endDateOfMonth = new Date(filter.year, filter.month + 1, 0);
            const summary = {};
            for (const emp of employees) {
                const q = query(collection(db, 'attendance'), where("employeeId", "==", emp.id), where("date", ">=", toYYYYMMDD(startDateOfMonth)), where("date", "<=", toYYYYMMDD(endDateOfMonth)));
                const attSnap = await getDocs(q);
                const presentDays = attSnap.docs.filter(d => ['Present', 'Late', 'Completed'].includes(d.data().status)).length;
                summary[emp.id] = { present: presentDays, absent: 0 }; // Simplified absent logic for now
            }
            setAttendanceSummary(summary);
        };
        
        fetchAttendance();
        return () => unsubSalaries();
    }, [db, filter, employees]);
    
    // --- CORRECTED LOGIC FOR PAY & EDIT ---
    const handlePayNow = async (employee, record) => {
        if (!record || typeof record.netSalary !== 'number') { toast.error("Please 'Edit' and 'Save' the salary first."); return; }
        try {
            const salaryDocId = `${record.monthId}_${employee.id}`;
            await updateDoc(doc(db, `salaries`, salaryDocId), { status: 'Paid', paymentDate: Timestamp.now() });
            await addDoc(collection(db, `expenses`), { description: `Salary for ${employee.fullName}`, amount: record.netSalary, category: 'Salaries', date: Timestamp.now(), createdAt: Timestamp.now() });
            toast.success(`Salary for ${employee.fullName} paid!`);
        } catch (error) { toast.error(`Payment failed: ${error.message}`); }
    };
    
    const handleEditSalary = (employee) => {
         const salaryMonthId = `${filter.year}-${String(filter.month + 1).padStart(2, '0')}`;
         const absentDays = attendanceSummary[employee.id]?.absent || 0;
         const deductionPerDay = (employee.salary || 0) / 26; // Assuming 26 working days
         let record = salaryData[employee.id] || { monthId: salaryMonthId, employeeId: employee.id, salary: employee.salary, bonus: 0, notes: '', status: 'Unprocessed' };
         if (record.status === 'Unprocessed') { record.deductions = absentDays > 0 ? Math.round(absentDays * deductionPerDay) : 0; }
        setEditingSalaryRecord({ record, employee });
    };

    const handleUpdateSalary = async (updatedRecord) => {
        const salaryDocId = `${updatedRecord.monthId}_${updatedRecord.employeeId}`;
        try {
            await setDoc(doc(db, `salaries`, salaryDocId), updatedRecord, { merge: true });
            toast.success("Salary details updated.");
        } catch (error) { toast.error(`Failed to update salary: ${error.message}`); } 
        finally { setEditingSalaryRecord(null); }
    };

    const totalSalary = employees.reduce((acc, emp) => acc + (emp.salary || 0), 0);
    
    return (
        <div className="container mx-auto">
            {editingSalaryRecord && <SalaryEditModal isOpen={!!editingSalaryRecord} onClose={() => setEditingSalaryRecord(null)} onSave={handleUpdateSalary} salaryRecord={editingSalaryRecord.record} employee={editingSalaryRecord.employee} />}
            <div className="mb-8"><h1 className="admin-page-title">Salary Management</h1><p className="admin-page-sub">Process and track employee salaries for the selected month.</p></div>
            
            <div className="bg-white rounded-xl shadow-md p-4 mb-8">
                <div className="flex justify-end items-center gap-4">
                    <select value={filter.month} onChange={e => setFilter({...filter, month: parseInt(e.target.value)})} className="p-2 border rounded-lg bg-gray-50">{Array.from({ length: 12 }, (_, i) => <option key={i} value={i}>{new Date(0, i).toLocaleString('en-US', { month: 'long' })}</option>)}</select>
                    <select value={filter.year} onChange={e => setFilter({...filter, year: parseInt(e.target.value)})} className="p-2 border rounded-lg bg-gray-50">{Array.from({ length: 5 }, (_, i) => <option key={i} value={new Date().getFullYear() - i}>{new Date().getFullYear() - i}</option>)}</select>
                </div>
            </div>
            
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    {isLoading ? <LoadingState label="Loading salaries" /> : (
                        <table className="w-full text-sm">
                             <thead className="text-xs text-gray-500 uppercase bg-gray-50"><tr><th className="px-6 py-3 text-left">Employee</th><th className="px-6 py-3 text-center">Attendance (P/A)</th><th className="px-6 py-3 text-right">Basic Salary</th><th className="px-6 py-3 text-right">Net Salary</th><th className="px-6 py-3 text-center">Status</th><th className="px-6 py-3 text-center">Actions</th></tr></thead>
                            <tbody className="divide-y divide-gray-200">
                                {employees.map(emp => {
                                    const record = salaryData[emp.id];
                                    const present = attendanceSummary[emp.id]?.present || 0;
                                    const absent = attendanceSummary[emp.id]?.absent || 0;
                                    return (
                                        <tr key={emp.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-3 whitespace-nowrap"><div className="flex items-center gap-4"><img className="h-10 w-10 rounded-full object-cover" src={emp.photoURL} alt={emp.fullName}/><div><div className="font-semibold text-gray-900">{emp.fullName}</div><div className="text-xs text-gray-500">{emp.designation}</div></div></div></td>
                                            <td className="px-6 py-3 text-center"><span className="font-semibold text-green-700">{present}</span> / <span className="text-red-700">{absent}</span></td>
                                            <td className="px-6 py-3 text-right text-gray-700 font-mono">{(emp.salary || 0).toLocaleString()}</td>
                                            <td className="px-6 py-3 text-right text-gray-900 font-bold font-mono">{record?.netSalary?.toLocaleString() || 'N/A'}</td>
                                            <td className="px-6 py-3 text-center"><span className={`px-2 py-1 text-xs font-semibold rounded-full ${record?.status === 'Paid' ? 'bg-green-100 text-green-800' : record?.status === 'Processed' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'}`}>{record?.status || 'Unprocessed'}</span></td>
                                            <td className="px-6 py-3 text-center space-x-2">
                                                <button onClick={() => handlePayNow(emp, record)} disabled={record?.status === 'Paid'} className="text-sm bg-green-100 text-green-800 font-semibold py-2 px-3 rounded-lg hover:bg-green-200 disabled:bg-gray-200 disabled:text-gray-400">Pay</button>
                                                <button onClick={() => handleEditSalary(emp)} disabled={record?.status === 'Paid'} className="text-sm bg-gray-200 text-gray-800 font-semibold py-2 px-3 rounded-lg hover:bg-gray-300 disabled:bg-gray-200 disabled:text-gray-400">Edit</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                             <tfoot className="bg-gray-100 font-bold"><tr className="text-gray-900"><td colSpan="2" className="px-6 py-4 text-right">Total Monthly Basic Salaries:</td><td className="px-6 py-4 text-right font-mono">{totalSalary.toLocaleString()}</td><td colSpan="3"></td></tr></tfoot>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SalaryManagementSection;